<template>
  <div class="sales-table">
    <div class="row filters mb-4">
      <div class="col-xl-2 col-md-6 col-sm-6">
        <b-field>
          <app-autocomplete
            placeholder="Filtrar por Rede"
            field="name"
            :searchFunction="searchNetworkFn"
            :resourceIdFunction="resourceNetworkFn"
            v-model="filters.network_id"
            @selected="load"
          ></app-autocomplete>
        </b-field>
      </div>
      <div class="col-xl-2 col-md-6 col-sm-6">
        <b-field>
          <b-datepicker
            @input="load"
            v-model="datesFilter"
            :date-formatter="bDateRangeFormatter"
            :years-range="[-100, 100]"
            placeholder="Filtrar por período"
            trap-focus
            range
          >
            <button
              @click="
                datesFilter = null;
                load();
              "
              v-app-button.icon-left.clear
            >
              <b-icon size="is-small" icon="close"></b-icon>Limpar
            </button>
          </b-datepicker>
        </b-field>
      </div>
      <div class="col-xl-2 col-md-6 col-sm-6">
        <b-field>
          <b-select
            @input="load"
            v-model="filters.family"
            placeholder="Filtrar por Família"
          >
            <option :value="undefined">Todas</option>
            <option
              v-for="family in families"
              :key="family.id"
              :value="family.name"
              >{{ family.name }}</option
            >
          </b-select>
        </b-field>
      </div>
      <div class="col-xl-2 col-md-6 col-sm-6">
        <app-input
          @input="onInput"
          v-model="filters.term"
          placeholder="Pesquisar"
        ></app-input>
      </div>
    </div>

    <b-table
      :data="data"
      :loading="loading"
      paginated
      backend-pagination
      :total="total"
      :per-page="perPage"
      @page-change="onPageChange"
      aria-next-label="Próxima Página"
      aria-previous-label="Página Anterior"
      aria-page-label="Página"
      aria-current-label="Página Atual"
    >
      <template slot-scope="props">
        <b-table-column field="id" label="#" numeric>{{
          props.row.id
        }}</b-table-column>
        <b-table-column field="datetime" label="Data">{{
          props.row.sale_date | date('LL')
        }}</b-table-column>
        <b-table-column field="network" label="Rede">{{
          props.row.network.name
        }}</b-table-column>
        <b-table-column field="seller" label="Vendedor">
          <div>{{ props.row.seller_name }}</div>
          <div class="sub-text">
            {{ props.row.seller_cpf | VMask('###.###.###-##') }}
          </div>
        </b-table-column>
        <b-table-column field="product" label="Produto">
          <div>{{ props.row.product_description }}</div>
          <div class="sub-text">{{ props.row.product_family }}</div>
          <div class="sub-text">EAN {{ props.row.ean_code }}</div>
        </b-table-column>
        <b-table-column field="invoice" label="NF" centered>{{
          props.row.invoice_coupon_number
        }}</b-table-column>
        <b-table-column field="quantity" label="QTDE" centered>{{
          props.row.quantity
        }}</b-table-column>
        <b-table-column field="points" label="Pontos" centered>{{
          props.row.points
        }}</b-table-column>

        <b-table-column label="Opções">
          <button
            @click="show(props.row.id)"
            v-app-button.icon-only
            v-tooltip.bottom="'Visualizar'"
          >
            <b-icon size="is-small" icon="magnify" />
          </button>
          <button
            @click="onClickDeleteSale(props.row.id)"
            v-app-button.icon-only
            v-tooltip.bottom="'Excluir'"
            color="danger"
          >
            <b-icon size="is-small" icon="delete" />
          </button>
        </b-table-column>
      </template>
      <template slot="empty">
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large"></b-icon>
            </p>
            <p>Nenhum registro encontrado.</p>
          </div>
        </section>
      </template>
    </b-table>
  </div>
</template>

<script>
import NetworkService from '@/services/network.service';
import ProductService from '@/services/product.service';
import SalesService from '@/services/sale.service';
import ToastMixin from '@/mixins/ToastifyMixin';
import BuefyMixin from '@/mixins/BuefyMixin';

import debounce from 'lodash/debounce';

export default {
  name: 'sales-table',
  mixins: [ToastMixin, BuefyMixin],
  props: ['importId'],
  data: () => {
    return {
      data: [],
      filters: { family: '' },
      datesFilter: null,
      families: [],
      total: 0,
      loading: false,
      page: 1,
      perPage: 10,
    };
  },
  computed: {
    requestFilters() {
      const [start_date, end_date] = this.datesFilter || [];

      return {
        ...this.filters,
        import_id: this.importId,
        start_date,
        end_date,
      };
    },
  },
  methods: {
    load() {
      this.loading = true;

      SalesService.search({
        page: this.page,
        perPage: this.perPage,
        ...this.requestFilters,
      })
        .then((response) => {
          const { total, data } = response.data;

          this.total = total;
          this.data = data;
        })
        .catch(() => this.toastify('Erro ao carregar Vendas.', 'error'))
        .finally(() => (this.loading = false));
    },
    onInput: debounce(function () {
      this.load();
    }, 400),
    show(id) {
      this.$router.push(`/sales/${id}`);
    },
    onClickDeleteSale(id) {
      this.$buefy.dialog.confirm({
        title: 'Excluindo Venda',
        message:
          'Tem certeza que deseja <b>excluir</b> esta Venda? Essa ação não pode ser desfeita.',
        cancelText: 'Cancelar',
        confirmText: 'Excluir Venda',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.deleteSale(id),
      });
    },
    deleteSale(id) {
      SalesService.delete(id)
        .then(() => {
          this.toastify('Venda excluída com sucesso.');
          this.load();
        })
        .catch(({ response }) => {
          this.toastify(
            response.data.message || 'Erro ao tentar excluir Venda.',
            'error'
          );
        });
    },
    onPageChange(page) {
      this.page = page;
      this.load();
    },
    loadFamilies() {
      ProductService.getFamilies().then(({ data }) => {
        this.families = data.map((family, id) => ({ id, name: family }));
      });
    },
    searchNetworkFn: NetworkService.search,
    resourceNetworkFn: NetworkService.getId,
  },
  mounted() {
    if (localStorage.salesTableNetworkId) {
      this.filters = {
        ...this.filters,
        network_id: localStorage.salesTableNetworkId,
      };
    }

    if (localStorage.salesTableDates) {
      const date = [
        new Date(localStorage.salesTableDates.split(',')[0]),
        new Date(localStorage.salesTableDates.split(',')[1]),
      ];
      this.datesFilter = date;
    }

    this.filters = {
      ...this.filters,
      family: localStorage.salesTableFamily || undefined,
    };

    if (localStorage.salesTableTerm) {
      this.filters = {
        ...this.filters,
        term: localStorage.salesTableTerm,
      };
    }

    this.load();
    this.loadFamilies();
  },
  watch: {
    datesFilter: (newValue) => {
      localStorage.salesTableDates = newValue;
    },
    'filters.family': (newValue) => {
      localStorage.salesTableFamily = newValue || '';
    },
    'filters.network_id': (newValue) => {
      localStorage.salesTableNetworkId = newValue || '';
    },
    'filters.term': (newValue) => {
      localStorage.salesTableTerm = newValue;
    },
  },
};
</script>

<style lang="scss" scoped>
.sub-text {
  color: #aaa;
  font-size: 0.8em;
}
</style>
